import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, Diamond, Lock } from "lucide-react";
import {
  startSession, listenFetch, coverUrl, SESSION_KEY, INVITE_KEY,
} from "@/lib/listenApi";
import { ListenShell } from "./listenShared";
import * as offline from "@/lib/offline";
import { ListenWelcome } from "./ListenWelcome";
import { ListenLibrary } from "./ListenLibrary";
import { ListenPlayer } from "./ListenPlayer";

function Center({ children }) {
  return (
    <ListenShell>
      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">{children}</div>
    </ListenShell>
  );
}

// Inject listener-only <head> config (title, noindex, PWA manifest) + SW.
function useListenerHead(inviteToken) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Beta Listener";
    // Remember the invite token so the installable manifest can re-authenticate
    // the home-screen icon even after the token is stripped from the address bar.
    if (inviteToken) { try { localStorage.setItem(INVITE_KEY, inviteToken); } catch { /* ignore */ } }
    let manifestToken = inviteToken;
    try { manifestToken = manifestToken || localStorage.getItem(INVITE_KEY); } catch { /* ignore */ }
    const manifestHref = manifestToken
      ? `/api/listen/app-manifest?token=${encodeURIComponent(manifestToken)}`
      : "/listen-manifest.json";
    const added = [];
    const add = (tag, attrs) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
      return el;
    };
    add("meta", { name: "robots", content: "noindex, noarchive, nofollow" });
    add("link", { rel: "manifest", href: manifestHref, "data-listen": "1" });
    add("link", { rel: "apple-touch-icon", href: "/listen-apple-touch.png" });
    add("meta", { name: "apple-mobile-web-app-capable", content: "yes" });
    add("meta", { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    add("meta", { name: "apple-mobile-web-app-title", content: "Beta Listener" });
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta?.getAttribute("content");
    if (themeMeta) themeMeta.setAttribute("content", "#07090d");

    if ("serviceWorker" in navigator) {
      const hadController = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.register("/listen-sw.js", { scope: "/listen" })
        .then((reg) => {
          reg.update();
          if (reg.waiting) reg.waiting.postMessage("SKIP_WAITING");
          reg.addEventListener("updatefound", () => {
            const nw = reg.installing;
            if (!nw) return;
            nw.addEventListener("statechange", () => {
              if (nw.state === "installed" && navigator.serviceWorker.controller) {
                nw.postMessage("SKIP_WAITING");
              }
            });
          });
        })
        .catch(() => {});
      // When an UPDATED worker takes control, reload once to run the fresh code.
      // (Skip on first-ever install, where there was no previous controller.)
      let reloaded = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloaded || !hadController) return;
        reloaded = true;
        window.location.reload();
      });
    }
    return () => {
      document.title = prevTitle;
      added.forEach((el) => el.remove());
      if (themeMeta && prevTheme) themeMeta.setAttribute("content", prevTheme);
    };
  }, [inviteToken]);
}

export default function ListenApp() {
  const { token: inviteToken } = useParams();
  const [sp] = useSearchParams();
  const previewToken = sp.get("preview");

  const [phase, setPhase] = useState("loading"); // loading|no-invite|revoked|welcome|library|player
  const [session, setSession] = useState(null);
  const [preview, setPreview] = useState(false);
  const [data, setData] = useState(null);
  const [, setErrMsg] = useState("");
  const [player, setPlayer] = useState(null); // {index, position}
  const [online, setOnline] = useState(navigator.onLine);
  const [syncState, setSyncState] = useState("synced"); // synced|syncing|pending|offline
  const [coverSrc, setCoverSrc] = useState(null);
  const coverUrlRef = useRef(null);
  const bootstrapped = useRef(false);

  useListenerHead(inviteToken);

  // Resolve the displayable cover: cached blob (offline-friendly) or network,
  // and cache it locally when online so it survives offline.
  const coverVersion = data?.cover_version || null;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (coverUrlRef.current) { URL.revokeObjectURL(coverUrlRef.current); coverUrlRef.current = null; }
      if (!coverVersion) { setCoverSrc(null); return; }
      const blobUrl = await offline.getCoverBlobUrl(coverVersion);
      if (cancelled) { if (blobUrl) URL.revokeObjectURL(blobUrl); return; }
      if (blobUrl) { coverUrlRef.current = blobUrl; setCoverSrc(blobUrl); }
      else if (navigator.onLine && session) { setCoverSrc(coverUrl(session, coverVersion)); offline.cacheCover(session, coverVersion); }
      else setCoverSrc(null);
    })();
    return () => { cancelled = true; };
  }, [coverVersion, session]);
  useEffect(() => () => { if (coverUrlRef.current) URL.revokeObjectURL(coverUrlRef.current); }, []);
  const coverArtworkUrl = coverVersion && session ? coverUrl(session, coverVersion) : null;

  const mergeLocal = useCallback(async (me) => {
    if (me.preview || !me.release) { setData(me); setPreview(me.preview); return me; }
    const relId = me.release.release_id;
    const prog = await offline.localProgress(relId);
    const items = me.release.items.map((it) => {
      const l = prog[it.item_id];
      if (l && (!it.updated_at || (l.updated_at || "") >= (it.updated_at || ""))) {
        const dur = it.duration || l.duration || 0;
        const completed = it.completed || l.completed;
        const pos = completed ? it.position : l.position;
        return { ...it, position: pos, completed,
          status: completed ? "completed" : (pos > 0 ? "in_progress" : "not_started"),
          percent: dur ? Math.min(100, (pos / dur) * 100) : 0 };
      }
      return it;
    });
    me.release.items = items;
    const localMap = {}; items.forEach((it) => { localMap[it.item_id] = { position: it.position, completed: it.completed }; });
    const { metaGet } = await import("@/lib/listenDB");
    const ll = await metaGet("last_listen_" + relId);
    me.continue = offline.computeContinue(items, localMap, ll) || me.continue;
    await offline.cacheLibrary(relId, { ...me });
    await offline.cacheActiveRelease(relId);
    setData(me); setPreview(false);
    return me;
  }, []);

  const loadMe = useCallback(async (tok) => {
    try {
      const me = await listenFetch("/listen/me", tok);
      return await mergeLocal(me);
    } catch (e) {
      if ((e.status === 401 || e.status === 403 || e.status === 404) && navigator.onLine) throw e;
      // Offline (or transient) — fall back to cached library + local progress.
      const relId = await offline.activeRelease();
      const cached = relId ? await offline.cachedLibrary(relId) : null;
      if (cached && cached.release) {
        const prog = await offline.localProgress(relId);
        cached.release.items = cached.release.items.map((it) => {
          const l = prog[it.item_id]; if (!l) return it;
          const dur = it.duration || l.duration || 0; const completed = it.completed || l.completed;
          return { ...it, position: completed ? it.position : l.position, completed,
            status: completed ? "completed" : (l.position > 0 ? "in_progress" : "not_started"),
            percent: dur ? Math.min(100, ((completed ? it.position : l.position) / dur) * 100) : 0 };
        });
        const lm = {}; cached.release.items.forEach((it) => { lm[it.item_id] = { position: it.position, completed: it.completed }; });
        const { metaGet } = await import("@/lib/listenDB");
        cached.continue = offline.computeContinue(cached.release.items, lm, await metaGet("last_listen_" + relId));
        cached.onboarded = true;
        setData(cached); setPreview(false);
        return cached;
      }
      throw e;
    }
  }, [mergeLocal]);

  const sync = useCallback(async (tok) => {
    if (!navigator.onLine || !tok) return;
    setSyncState("syncing");
    const r = await offline.flushOutbox(tok);
    try { await offline.flushFeedback(tok); } catch { /* ignore */ }
    setSyncState(r.pending > 0 ? "pending" : "synced");
    try { await loadMe(tok); } catch { /* ignore */ }
  }, [loadMe]);

  useEffect(() => {
    const on = () => { setOnline(true); if (session) sync(session); };
    const off = () => { setOnline(false); setSyncState("offline"); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    if (!navigator.onLine) setSyncState("offline");
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [session, sync]);

  // Resolve the session once.
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    (async () => {
      try {
        if (previewToken) {
          setSession(previewToken);
          const me = await loadMe(previewToken);
          setPhase(me.onboarded ? "library" : "welcome");
          return;
        }
        if (inviteToken) {
          const { session_token } = await startSession(inviteToken);
          localStorage.setItem(SESSION_KEY, session_token);
          setSession(session_token);
          // Strip the token from the address bar WITHOUT a router navigation:
          // navigate() would switch Route elements and remount this component,
          // causing a second concurrent bootstrap (and an IndexedDB deadlock).
          window.history.replaceState(null, "", "/listen");
          const me = await loadMe(session_token);
          setPhase(me.onboarded ? "library" : "welcome");
          return;
        }
        const saved = localStorage.getItem(SESSION_KEY);
        if (!saved) { setPhase("no-invite"); return; }
        setSession(saved);
        const me = await loadMe(saved);
        setPhase(me.onboarded ? "library" : "welcome");
      } catch (e) {
        if (e.status === 403) setPhase("revoked");
        else if (e.status === 401 || e.status === 404) {
          if (!previewToken && !inviteToken) localStorage.removeItem(SESSION_KEY);
          setErrMsg(e.detail || "");
          setPhase(previewToken ? "revoked" : "no-invite");
        } else { setErrMsg(e.detail || "Unable to load."); setPhase("no-invite"); }
      }
    })();
  }, [inviteToken, previewToken, loadMe]);

  useEffect(() => {
    if (session && !preview && navigator.onLine) {
      Promise.all([offline.outboxCount(), offline.pendingFeedbackCount()])
        .then(([o, f]) => { if (o > 0 || f > 0) sync(session); });
    }
  }, [session, preview, sync]);

  const startListening = async () => {
    try { await listenFetch("/listen/onboarded", session, { method: "POST" }); } catch { /* ignore */ }
    const me = await loadMe(session);
    setData(me);
    // Jump straight into the natural starting point / continue location.
    const items = me.release?.items || [];
    const cont = me.continue;
    if (items.length && cont) {
      const idx = items.findIndex((i) => i.item_id === cont.item_id);
      setPlayer({ index: idx >= 0 ? idx : 0, position: cont.position || 0 });
      setPhase("player");
    } else {
      setPhase("library");
    }
  };

  const openPlayer = (itemId, position) => {
    const items = data.release?.items || [];
    const idx = items.findIndex((i) => i.item_id === itemId);
    setPlayer({ index: idx >= 0 ? idx : 0, position: position || 0 });
    setPhase("player");
  };

  const exitPlayer = async (refresh) => {
    setPhase("library");
    setPlayer(null);
    if (refresh) { try { await loadMe(session); } catch { /* ignore */ } }
  };

  if (phase === "loading") {
    return <Center><Loader2 className="h-6 w-6 animate-spin text-teal-400" /></Center>;
  }
  if (phase === "no-invite") {
    return (
      <Center>
        <Diamond className="h-12 w-12 text-slate-700" strokeWidth={1.1} />
        <h1 data-testid="listen-no-invite" className="mt-6 font-serif text-2xl text-slate-200">Open your invitation</h1>
        <p className="mt-3 max-w-xs text-sm text-slate-500">Use the private link you were sent to begin listening.</p>
      </Center>
    );
  }
  if (phase === "revoked") {
    return (
      <Center>
        <Lock className="h-11 w-11 text-slate-700" strokeWidth={1.2} />
        <h1 data-testid="listen-revoked" className="mt-6 font-serif text-2xl text-slate-200">Private access unavailable</h1>
        <p className="mt-3 max-w-xs text-sm text-slate-500">This preview is no longer available.</p>
      </Center>
    );
  }
  if (phase === "welcome") {
    return (
      <ListenWelcome
        bookTitle={data?.book_title}
        author={data?.author}
        name={data?.display_name}
        preview={preview}
        cover={coverSrc}
        onStart={startListening}
      />
    );
  }
  if (phase === "player" && player) {
    return (
      <ListenPlayer
        items={data.release.items}
        releaseId={data.release.release_id}
        startIndex={player.index}
        startPosition={player.position}
        token={session}
        preview={preview}
        online={online}
        cover={coverSrc}
        coverArtworkUrl={coverArtworkUrl}
        meta={{ book_title: data.book_title, author: data.author }}
        onExit={exitPlayer}
      />
    );
  }
  return <ListenLibrary data={data} preview={preview} online={online} syncState={syncState} token={session} cover={coverSrc} onPlay={openPlayer} />;
}
